import React, {useEffect, useState} from "react";

const S_COLOR = '#d4af37';

/**
 * Lowercase, strip non-word characters, replace whitespace with `-`.
 * Used to build a stable `id` for any heading that lacks one so the TOC can
 * anchor-jump to it.
 */
export function slugify(text: string) {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-");
}

export interface ResourcesTableOfContentsProps {
  /** Ref to the rendered article container. The TOC scans this for `h2`/`h3`. */
  contentRef: React.RefObject<HTMLDivElement | null>;
  /**
   * Anything whose change should trigger a re-scan of `contentRef` — typically
   * the article's HTML string. Used as the dependency for the heading-collection
   * effect since refs themselves don't trigger re-renders.
   */
  articleDep: any;
}

/**
 * Sticky table of contents rendered alongside a guide / resource article.
 *
 * Responsibilities:
 *   1. Scans `contentRef` for `<h2>` and `<h3>` elements after the article
 *      renders, assigning a slugified `id` to any heading that lacks one.
 *   2. Renders a clickable list; clicking smooth-scrolls the page so the
 *      heading lands ~80px below the viewport top.
 *   3. Tracks scroll position and marks the nearest heading above the fold
 *      as "active".
 *   4. Renders a thin progress bar pinned just below the global header.
 *
 * The component is purely view-layer — it neither owns the article HTML nor
 * mutates anything beyond the headings' `id` attributes.
 */
export default function ResourcesTableOfContents({ contentRef, articleDep }: ResourcesTableOfContentsProps) {
  const [headings, setHeadings] = useState<
    { id: string; text: string; level: number; top: number }[]
  >([]);
  const [activeId, setActiveId] = useState<string>('');
  const [progress, setProgress] = useState(0);

  const handleJump = (hid: string) => {
    const el = document.getElementById(hid);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 80;
    window.scrollTo({ top, behavior: "smooth" });
  };

  // for archoring tags to specific locations
  useEffect(() => {
    const onScroll = () => {
      const doc = document.documentElement;
      const scrollTop = window.pageYOffset || doc.scrollTop;
      const scrollHeight = doc.scrollHeight - doc.clientHeight;
      const pct = scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0;
      setProgress(pct);

      let current = "";
      for (const h of headings) {
        if (scrollTop + 140 >= h.top) current = h.id;
        else break;
      }
      if (current) setActiveId(current);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [headings]);

  // collect headings for table of contents
  // for each article page, we use h2,h3 for session headers, so we are collecting these html elements to get titles
  useEffect(() => {
    if (!contentRef.current) return;
    const hEls = Array.from(
      contentRef.current.querySelectorAll("h2, h3")
    ) as HTMLElement[];

    const items = hEls.map((el) => {
      const text = el.textContent || "";
      const id = el.id || slugify(text);
      el.id = id;
      return { id, text, level: el.tagName === "H3" ? 3 : 2, top: el.offsetTop };
    });
    setHeadings(items);
  }, [articleDep]);

  return (
    <>
      <h6>Table of contents</h6>
      {headings.length === 0 ? (
        <p className="text-muted">No sections detected.</p>
      ) : (
        <div className="toc-simple">
          {headings.map((h) => (
            <div
              key={h.id}
              className={`toc-entry ${activeId === h.id ? "active" : ""}`}
              onClick={() => handleJump(h.id)}
            >
              <div className="bar" />
              <div className="label">{h.text}</div>
            </div>
          ))}
        </div>
      )}
      {/* scroll the progress bar at bottom */}
      <div
        style={{
          position: "fixed",
          top: '83px',
          left: 0,
          height: 4,
          width: `${progress}%`,
          background: S_COLOR,
          zIndex: 2000,
          transition: "width 0.1s linear",
        }}
      />
      <style>{
        `.toc-float-btn {
          position: fixed;
          left: 20px;
          bottom: 40px;   /* Moved from center-left to bottom-left */
          border-radius: 50px;
          border: 2px solid ${S_COLOR};
          color: black;
          background: white;
          z-index: 2001;
          padding: 0.6rem 1.25rem;
          transition: all 0.3s ease;
          animation: float 3s ease-in-out infinite;
        }
        .toc-float-btn:hover {
          background: ${S_COLOR};
          color: white;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-4px); }
        }
        .toc-simple {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding-top: 0.5rem;
        }
        .toc-entry {
          display: flex;
          align-items: center;
          cursor: pointer;
          transition: all 0.25s ease;
        }
        .toc-entry .bar {
          width: 4px;
          height: 20px;
          background-color: ${S_COLOR};
          border-radius: 2px;
          margin-right: 12px;
          transition: all 0.2s ease;
        }
        .toc-entry .label {
          font-size: 0.95rem;
          color: #333;
          transition: color 0.2s ease;
        }
        .toc-entry:hover .label {
          color: ${S_COLOR};
        }
        .toc-entry.active .bar {
          height: 28px;
          background-color: ${S_COLOR};
        }
        .toc-entry.active .label {
          color: ${S_COLOR};
          font-weight: 600;
        }
      `}
      </style>
    </>
  );
}
